import type { CSSProperties } from 'react';
import styled, { keyframes } from 'styled-components';

const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
  width: 100%;
  padding: ${({ theme }) => theme.spacing[8]};
`;

const SkeletonRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  width: 100%;
`;

const SkeletonBox = styled.div<{ width?: string; height?: string; borderRadius?: string }>`
  background: ${({ theme }) =>
    theme.isLight
      ? `linear-gradient(to right, ${theme.colors.backgroundSecondary} 8%, ${theme.colors.backgroundTertiary} 18%, ${theme.colors.backgroundSecondary} 33%)`
      : `linear-gradient(to right, ${theme.colors.backgroundSecondary} 8%, ${theme.colors.backgroundTertiary} 18%, ${theme.colors.backgroundSecondary} 33%)`
  };
  background-size: 800px 104px;
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || '60px'};
  border-radius: ${({ borderRadius, theme }) => borderRadius || theme.borders.radius.lg};
  animation: ${shimmer} 1.2s ease-in-out infinite;
  position: relative;
  overflow: hidden;
`;

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
  width: 100%;
`;


const toCssSize = (value?: number | string) => {
  if (value === undefined) return undefined;
  return typeof value === 'number' ? `${value}px` : value;
};

interface SkeletonLoaderProps {
  variant?: 'default' | 'cards' | 'list' | 'dashboard' | 'leads' | 'reports' | 'profile' | 'sales' | 'integrations' | 'botSettings' | 'nucleoNeural' | 'braip' | 'box';
  height?: number | string;
  width?: number | string;
  borderRadius?: string;
  style?: CSSProperties;
}

export const SkeletonLoader = ({ variant = 'default', height, width, borderRadius, style }: SkeletonLoaderProps) => {
  if (variant === 'box') {
    return (
      <SkeletonBox
        height={toCssSize(height)}
        width={toCssSize(width) ?? '100%'}
        borderRadius={borderRadius}
        style={style}
      />
    );
  }

  if (variant === 'profile') {
    // Estrutura profissional para Profile: Header + Avatar + Seções de Info
    return (
      <SkeletonWrapper style={{ padding: 0 }}>
        {/* Header com título e descrição */}
        <div style={{ marginBottom: 32 }}>
          <SkeletonBox height="48px" width="280px" style={{ marginBottom: 12 }} />
          <SkeletonBox height="24px" width="420px" />
        </div>

        {/* Card Principal do Perfil */}
        <div style={{
          background: 'var(--skeleton-bg, rgba(255,255,255,0.03))',
          border: '1px solid var(--skeleton-border, rgba(255,255,255,0.1))',
          borderRadius: 16,
          padding: 32,
          marginBottom: 24
        }}>
          {/* Avatar centralizado */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 32 }}>
            <SkeletonBox height="120px" width="120px" borderRadius="50%" style={{ marginBottom: 16 }} />
            <SkeletonBox height="28px" width="200px" style={{ marginBottom: 8 }} />
            <SkeletonBox height="20px" width="160px" />
          </div>

          {/* Grid de informações 2x2 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
            <div>
              <SkeletonBox height="16px" width="80px" style={{ marginBottom: 8 }} />
              <SkeletonBox height="48px" width="100%" />
            </div>
            <div>
              <SkeletonBox height="16px" width="80px" style={{ marginBottom: 8 }} />
              <SkeletonBox height="48px" width="100%" />
            </div>
            <div>
              <SkeletonBox height="16px" width="80px" style={{ marginBottom: 8 }} />
              <SkeletonBox height="48px" width="100%" />
            </div>
            <div>
              <SkeletonBox height="16px" width="80px" style={{ marginBottom: 8 }} />
              <SkeletonBox height="48px" width="100%" />
            </div>
          </div>

          {/* Botão de ação */}
          <SkeletonBox height="48px" width="200px" style={{ margin: '0 auto' }} />
        </div>

        {/* Seção de Configurações */}
        <div style={{
          background: 'var(--skeleton-bg, rgba(255,255,255,0.03))',
          border: '1px solid var(--skeleton-border, rgba(255,255,255,0.1))',
          borderRadius: 16,
          padding: 32
        }}>
          <SkeletonBox height="28px" width="220px" style={{ marginBottom: 24 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <SkeletonBox height="64px" width="100%" />
            <SkeletonBox height="64px" width="100%" />
            <SkeletonBox height="64px" width="100%" />
          </div>
        </div>
      </SkeletonWrapper>
    );
  }

  if (variant === 'leads') {
    // Estrutura profissional para Leads: Header + Stats + Tabela
    return (
      <SkeletonWrapper style={{ padding: 0 }}>
        {/* Header com título e botão */}
        <SkeletonRow style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <div>
            <SkeletonBox height="48px" width="380px" style={{ marginBottom: 8 }} />
            <SkeletonBox height="24px" width="460px" />
          </div>
          <SkeletonBox height="44px" width="140px" />
        </SkeletonRow>

        {/* Stats Cards (4 cards) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
          {[...Array(4)].map((_, i) => (
            <SkeletonBox key={i} height="110px" width="100%" />
          ))}
        </div>

        {/* Toolbar de filtros e busca */}
        <SkeletonRow style={{ justifyContent: 'space-between', marginBottom: 24 }}>
          <SkeletonRow style={{ gap: 12, flex: 'none' }}>
            <SkeletonBox height="40px" width="140px" />
            <SkeletonBox height="40px" width="140px" />
          </SkeletonRow>
          <SkeletonBox height="40px" width="280px" />
        </SkeletonRow>

        {/* Tabela de leads */}
        <div style={{
          background: 'var(--skeleton-bg, rgba(255,255,255,0.03))',
          border: '1px solid var(--skeleton-border, rgba(255,255,255,0.1))',
          borderRadius: 12,
          padding: 0,
          overflow: 'hidden'
        }}>
          {/* Header da tabela */}
          <SkeletonBox height="56px" width="100%" borderRadius="0" style={{ marginBottom: 0 }} />

          {/* Linhas da tabela */}
          {[...Array(8)].map((_, i) => (
            <SkeletonBox key={i} height="72px" width="100%" borderRadius="0" style={{ marginBottom: 1 }} />
          ))}
        </div>

        {/* Paginação */}
        <SkeletonRow style={{ justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
          <SkeletonBox height="36px" width="180px" />
          <SkeletonRow style={{ gap: 8, flex: 'none' }}>
            <SkeletonBox height="36px" width="36px" />
            <SkeletonBox height="36px" width="36px" />
            <SkeletonBox height="36px" width="36px" />
          </SkeletonRow>
        </SkeletonRow>
      </SkeletonWrapper>
    );
  }

  if (variant === 'reports') {
    // Estrutura profissional para Reports: Header + Filtros + Gráficos + Métricas
    return (
      <SkeletonWrapper style={{ padding: 0 }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <SkeletonBox height="48px" width="320px" style={{ marginBottom: 12 }} />
          <SkeletonBox height="24px" width="480px" />
        </div>

        {/* Toolbar de período e filtros */}
        <SkeletonRow style={{ gap: 12, marginBottom: 32, flexWrap: 'wrap' }}>
          {[...Array(5)].map((_, i) => (
            <SkeletonBox key={i} height="40px" width="120px" />
          ))}
        </SkeletonRow>

        {/* Cards de métricas principais (4 cards) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 32 }}>
          {[...Array(4)].map((_, i) => (
            <SkeletonBox key={i} height="120px" width="100%" />
          ))}
        </div>

        {/* Gráfico principal grande */}
        <SkeletonBox height="400px" width="100%" style={{ marginBottom: 32 }} />

        {/* Seção de comparativos (2 gráficos lado a lado) */}
        <SkeletonRow style={{ gap: 24, marginBottom: 32 }}>
          <SkeletonBox height="320px" width="100%" />
          <SkeletonBox height="320px" width="100%" />
        </SkeletonRow>

        {/* Tabela de detalhes */}
        <div style={{
          background: 'var(--skeleton-bg, rgba(255,255,255,0.03))',
          border: '1px solid var(--skeleton-border, rgba(255,255,255,0.1))',
          borderRadius: 12,
          overflow: 'hidden'
        }}>
          <SkeletonBox height="56px" width="100%" borderRadius="0" />
          {[...Array(6)].map((_, i) => (
            <SkeletonBox key={i} height="64px" width="100%" borderRadius="0" style={{ marginBottom: 1 }} />
          ))}
        </div>
      </SkeletonWrapper>
    );
  }

  if (variant === 'nucleoNeural') {
    // Estrutura para a página NucleoNeural: header, tabs, e layout de 2 colunas
    return (
      <SkeletonWrapper>
        {/* Header */}
        <SkeletonRow style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ flex: 1 }}>
            <SkeletonBox height="40px" width="250px" style={{ marginBottom: 8 }} />
            <SkeletonBox height="20px" width="350px" />
          </div>
          <SkeletonRow style={{ gap: 12, flex: 'none' }}>
            <SkeletonBox height="42px" width="180px" />
            <SkeletonBox height="42px" width="180px" />
          </SkeletonRow>
        </SkeletonRow>

        {/* Tabs */}
        <SkeletonRow style={{ gap: 24, borderBottom: '2px solid #2a2a2a', marginBottom: 32, padding: '0 8px' }}>
          <SkeletonBox height="24px" width="150px" style={{ marginBottom: 12, background: 'none' }} />
          <SkeletonBox height="24px" width="180px" style={{ marginBottom: 12, background: 'none' }} />
          <SkeletonBox height="24px" width="160px" style={{ marginBottom: 12, background: 'none' }} />
        </SkeletonRow>

        {/* Main Content: Sidebar + Editor */}
        <SkeletonRow style={{ gap: 24, height: 'calc(100vh - 300px)' }}>
          {/* Sidebar */}
          <div style={{ width: 320, minWidth: 320, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <SkeletonBox height="48px" width="100%" style={{ marginBottom: 8 }} />
            {[...Array(3)].map((_, i) => (
              <div key={i} style={{ marginBottom: 16 }}>
                <SkeletonBox height="16px" width="120px" style={{ marginBottom: 12, opacity: 0.5 }} />
                <SkeletonBox height="60px" width="100%" style={{ marginBottom: 8 }} />
                <SkeletonBox height="60px" width="100%" />
              </div>
            ))}
          </div>

          {/* Editor */}
          <div style={{ flex: 1 }}>
            <SkeletonBox height="100%" width="100%" />
          </div>
        </SkeletonRow>
      </SkeletonWrapper>
    );
  }

  if (variant === 'braip') {
    // Estrutura profissional para Braip: header, config card, stats, webhook history
    return (
      <SkeletonWrapper style={{ padding: 0 }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <SkeletonBox height="48px" width="280px" style={{ marginBottom: 12 }} />
          <SkeletonBox height="24px" width="520px" />
        </div>

        {/* Config Card */}
        <div style={{
          background: 'var(--skeleton-bg, rgba(255,255,255,0.03))',
          border: '1px solid var(--skeleton-border, rgba(255,255,255,0.1))',
          borderRadius: 16,
          padding: 32,
          marginBottom: 24
        }}>
          {/* Config Header */}
          <SkeletonRow style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <SkeletonRow style={{ gap: 12, flex: 'none' }}>
              <SkeletonBox height="24px" width="24px" borderRadius="4px" />
              <SkeletonBox height="28px" width="200px" />
            </SkeletonRow>
            <SkeletonBox height="32px" width="140px" borderRadius="8px" />
          </SkeletonRow>

          {/* Info Box */}
          <SkeletonBox height="180px" width="100%" style={{ marginBottom: 20 }} />

          {/* Form Fields */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div>
              <SkeletonBox height="16px" width="120px" style={{ marginBottom: 8 }} />
              <SkeletonBox height="48px" width="100%" />
            </div>
            <div>
              <SkeletonBox height="16px" width="180px" style={{ marginBottom: 8 }} />
              <SkeletonBox height="48px" width="100%" />
            </div>
            <div>
              <SkeletonBox height="16px" width="100px" style={{ marginBottom: 8 }} />
              <SkeletonBox height="56px" width="100%" />
            </div>
          </div>

          {/* Buttons */}
          <SkeletonRow style={{ gap: 12, marginTop: 24 }}>
            <SkeletonBox height="44px" width="140px" borderRadius="8px" />
            <SkeletonBox height="44px" width="180px" borderRadius="8px" />
          </SkeletonRow>
        </div>

        {/* Stats Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{
              background: 'var(--skeleton-bg, rgba(255,255,255,0.03))',
              border: '1px solid var(--skeleton-border, rgba(255,255,255,0.1))',
              borderRadius: 12,
              padding: 20
            }}>
              <SkeletonRow style={{ gap: 8, marginBottom: 12 }}>
                <SkeletonBox height="40px" width="40px" borderRadius="8px" />
                <SkeletonBox height="16px" width="100px" />
              </SkeletonRow>
              <SkeletonBox height="36px" width="80px" style={{ marginBottom: 8 }} />
              <SkeletonBox height="14px" width="120px" />
            </div>
          ))}
        </div>

        {/* Webhook History Card */}
        <div style={{
          background: 'var(--skeleton-bg, rgba(255,255,255,0.03))',
          border: '1px solid var(--skeleton-border, rgba(255,255,255,0.1))',
          borderRadius: 16,
          padding: 32
        }}>
          {/* History Header */}
          <SkeletonRow style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <SkeletonRow style={{ gap: 12, flex: 'none' }}>
              <SkeletonBox height="24px" width="24px" borderRadius="4px" />
              <SkeletonBox height="28px" width="220px" />
            </SkeletonRow>
            <SkeletonBox height="36px" width="120px" borderRadius="8px" />
          </SkeletonRow>

          {/* Table */}
          <div style={{ borderRadius: 12, overflow: 'hidden' }}>
            <SkeletonBox height="56px" width="100%" borderRadius="0" />
            {[...Array(5)].map((_, i) => (
              <SkeletonBox key={i} height="72px" width="100%" borderRadius="0" style={{ marginBottom: 1 }} />
            ))}
          </div>
        </div>
      </SkeletonWrapper>
    );
  }

  if (variant === 'sales') {
    // Estrutura profissional para Sales: header, stats, gráficos, tabela
    return (
      <SkeletonWrapper style={{ padding: 0 }}>
        {/* Header */}
        <SkeletonRow style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <div>
            <SkeletonBox height="48px" width="320px" style={{ marginBottom: 12 }} />
            <SkeletonBox height="24px" width="440px" />
          </div>
          <SkeletonBox height="44px" width="140px" />
        </SkeletonRow>

        {/* Stats Cards (4 cards) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 32 }}>
          {[...Array(4)].map((_, i) => (
            <SkeletonBox key={i} height="110px" width="100%" />
          ))}
        </div>

        {/* Gráficos lado a lado */}
        <SkeletonRow style={{ gap: 24, marginBottom: 32 }}>
          <SkeletonBox height="360px" width="65%" />
          <SkeletonBox height="360px" width="35%" />
        </SkeletonRow>

        {/* Toolbar da tabela */}
        <SkeletonRow style={{ justifyContent: 'space-between', marginBottom: 20 }}>
          <SkeletonBox height="32px" width="200px" />
          <SkeletonBox height="40px" width="280px" />
        </SkeletonRow>

        {/* Tabela de vendas */}
        <div style={{
          background: 'var(--skeleton-bg, rgba(255,255,255,0.03))',
          border: '1px solid var(--skeleton-border, rgba(255,255,255,0.1))',
          borderRadius: 12,
          overflow: 'hidden'
        }}>
          <SkeletonBox height="56px" width="100%" borderRadius="0" />
          {[...Array(7)].map((_, i) => (
            <SkeletonBox key={i} height="68px" width="100%" borderRadius="0" style={{ marginBottom: 1 }} />
          ))}
        </div>
      </SkeletonWrapper>
    );
  }

  if (variant === 'botSettings') {
    // Estrutura PERFEITA: simula cada seção da página de configurações
    return (
      <SkeletonWrapper style={{ padding: 0 }}>
        {/* HEADER: Title + Description + Actions (lado a lado) */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <SkeletonBox height="40px" width="280px" style={{ marginBottom: 8 }} />
            <SkeletonBox height="20px" width="400px" />
          </div>
          <SkeletonRow style={{ gap: 12 }}>
            <SkeletonBox height="42px" width="200px" />
            <SkeletonBox height="42px" width="180px" />
          </SkeletonRow>
        </div>

        {/* SEÇÃO 1: PERFIL DO USUÁRIO */}
        <div style={{
          background: 'var(--skeleton-bg, rgba(255,255,255,0.03))',
          border: '1px solid var(--skeleton-border, rgba(255,255,255,0.1))',
          borderRadius: 12,
          padding: 24,
          marginBottom: 24
        }}>
          {/* Section Header */}
          <SkeletonRow style={{ gap: 12, marginBottom: 24 }}>
            <SkeletonBox height="40px" width="40px" borderRadius="8px" />
            <div style={{ flex: 1 }}>
              <SkeletonBox height="24px" width="180px" style={{ marginBottom: 6 }} />
              <SkeletonBox height="16px" width="280px" />
            </div>
          </SkeletonRow>

          {/* ProfileSection: Avatar centralizado, depois inputs */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Avatar centralizado */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <SkeletonBox height="96px" width="96px" borderRadius="50%" />
            </div>

            {/* Inputs em grid 2x2 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
              <SkeletonRow style={{ gap: 16 }}>
                <SkeletonBox height="64px" width="100%" />
                <SkeletonBox height="64px" width="100%" />
              </SkeletonRow>
              <SkeletonRow style={{ gap: 16 }}>
                <SkeletonBox height="64px" width="100%" />
                <SkeletonBox height="64px" width="100%" />
              </SkeletonRow>
              <SkeletonRow style={{ gap: 16 }}>
                <SkeletonBox height="64px" width="100%" />
                <SkeletonBox height="64px" width="100%" />
              </SkeletonRow>
            </div>
          </div>
        </div>

        {/* SEÇÃO 2: CONFIGURAÇÕES DO BOT */}
        <div style={{
          background: 'var(--skeleton-bg, rgba(255,255,255,0.03))',
          border: '1px solid var(--skeleton-border, rgba(255,255,255,0.1))',
          borderRadius: 12,
          padding: 24,
          marginBottom: 24
        }}>
          {/* Section Header */}
          <SkeletonRow style={{ gap: 12, marginBottom: 24 }}>
            <SkeletonBox height="40px" width="40px" borderRadius="8px" />
            <div style={{ flex: 1 }}>
              <SkeletonBox height="24px" width="200px" style={{ marginBottom: 6 }} />
              <SkeletonBox height="16px" width="300px" />
            </div>
          </SkeletonRow>

          {/* Input único */}
          <SkeletonBox height="48px" width="100%" style={{ marginBottom: 12 }} />

          {/* Grid de switches 2x2 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <SkeletonBox height="72px" width="100%" />
            <SkeletonBox height="72px" width="100%" />
            <SkeletonBox height="72px" width="100%" />
            <SkeletonBox height="72px" width="100%" />
          </div>
        </div>

        {/* SEÇÃO 3: PREVIEW DO BOT */}
        <div style={{
          background: 'var(--skeleton-bg, rgba(255,255,255,0.03))',
          border: '1px solid var(--skeleton-border, rgba(255,255,255,0.1))',
          borderRadius: 12,
          padding: 24
        }}>
          {/* Section Header */}
          <SkeletonRow style={{ gap: 12, marginBottom: 24 }}>
            <SkeletonBox height="40px" width="40px" borderRadius="8px" />
            <div style={{ flex: 1 }}>
              <SkeletonBox height="24px" width="150px" style={{ marginBottom: 6 }} />
              <SkeletonBox height="16px" width="320px" />
            </div>
          </SkeletonRow>

          {/* Botão de cenário */}
          <SkeletonBox height="48px" width="200px" style={{ marginBottom: 24 }} />

          {/* Test Area: Painel esquerdo + Chat direito */}
          <SkeletonRow style={{ gap: 32, alignItems: 'flex-start' }}>
            {/* Painel de controle */}
            <div style={{ width: '420px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <SkeletonBox height="140px" width="100%" />
              <SkeletonBox height="48px" width="100%" />
            </div>
            {/* Área de chat */}
            <div style={{ flex: 1 }}>
              <SkeletonBox height="300px" width="100%" />
            </div>
          </SkeletonRow>
        </div>
      </SkeletonWrapper>
    );
  }

  if (variant === 'integrations') {
    // Estrutura visual semelhante à página de integrações: 2 colunas de cards
    return (
      <SkeletonWrapper>
        <SkeletonRow style={{ gap: '2rem' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {[...Array(4)].map((_, i) => (
              <SkeletonBox key={i} height="180px" />
            ))}
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {[...Array(4)].map((_, i) => (
              <SkeletonBox key={i} height="180px" />
            ))}
          </div>
        </SkeletonRow>
      </SkeletonWrapper>
    );
  }

  if (variant === 'cards') {
    return (
      <SkeletonWrapper>
        <SkeletonGrid>
          <SkeletonBox height="120px" />
          <SkeletonBox height="120px" />
          <SkeletonBox height="120px" />
          <SkeletonBox height="120px" />
        </SkeletonGrid>
      </SkeletonWrapper>
    );
  }

  if (variant === 'list') {
    return (
      <SkeletonWrapper>
        <SkeletonBox height="80px" />
        <SkeletonBox height="80px" />
        <SkeletonBox height="80px" />
        <SkeletonBox height="80px" />
      </SkeletonWrapper>
    );
  }

  if (variant === 'dashboard') {
    // Estrutura fiel à dashboard: header, toolbar, stats, funil, timeline, cards tempo real
    return (
      <SkeletonWrapper style={{ padding: 0 }}>
        {/* Header + Subtítulo */}
        <div style={{ marginBottom: 24 }}>
          <SkeletonBox height="48px" width="320px" style={{ marginBottom: 8 }} />
          <SkeletonBox height="24px" width="420px" />
        </div>
        {/* Toolbar de períodos */}
        <SkeletonRow style={{ gap: 16, marginBottom: 32 }}>
          {[...Array(4)].map((_, i) => (
            <SkeletonBox key={i} height="36px" width="100px" />
          ))}
        </SkeletonRow>
        {/* Section: Visão Geral de Performance (3x2 cards) */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ marginBottom: 16 }}>
            <SkeletonBox height="32px" width="260px" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {[...Array(6)].map((_, i) => (
              <SkeletonBox key={i} height="120px" width="100%" />
            ))}
          </div>
        </div>
        {/* Divider */}
        <SkeletonBox height="2px" width="100%" style={{ opacity: 0.2, margin: '32px 0' }} />
        {/* Section: Funil de Classificação (gráfico) */}
        <div style={{ marginBottom: 32 }}>
          <SkeletonBox height="32px" width="260px" style={{ marginBottom: 16 }} />
          <SkeletonBox height="220px" width="100%" />
        </div>
        {/* Divider */}
        <SkeletonBox height="2px" width="100%" style={{ opacity: 0.2, margin: '32px 0' }} />
        {/* Section: Atividade em Tempo Real (timeline + cards) */}
        <div style={{ marginBottom: 32 }}>
          <SkeletonBox height="32px" width="260px" style={{ marginBottom: 16 }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 32 }}>
            {/* Timeline */}
            <SkeletonBox height="320px" width="100%" />
            {/* Cards tempo real (2 colunas) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <SkeletonBox height="130px" width="100%" />
              <SkeletonBox height="130px" width="100%" />
            </div>
          </div>
        </div>
      </SkeletonWrapper>
    );
  }

  // Variant default
  return (
    <SkeletonWrapper>
      <SkeletonBox height="100px" />
      <SkeletonRow>
        <SkeletonBox height="200px" width="70%" />
        <SkeletonBox height="200px" width="30%" />
      </SkeletonRow>
      <SkeletonBox height="150px" />
    </SkeletonWrapper>
  );
};

export default SkeletonLoader;
